import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import { useGlobalContext } from "../../context/GlobalContext";
import {
  API_CHECK_ADVANCE_BALANCE,
  API_CLEAR_ADVANCES,
} from "../../utils/apis";
import { formatAmount, getCredentials } from "../../utils/helpers";
import useGet from "../../hooks/common/useGet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import useSet from "../../hooks/common/useSet";
import { Button } from "antd";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";

export const CheckAdvanceBalanceModal: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role_details?.[0]?.name;
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState<any[]>([]);
  const [clearing, setClearing] = useState(false); // loader for clearing invoices

  const { data: globalData, updateData } = useGlobalContext();
  const { openAdvanceBalance } = globalData || {};
  const {
    data: advanceBalance,
    loadData,
    loading,
    error: statusError,
    success: loadSuccess,
    reset,
  } = useGet();
  const { success, submitting, uploadData, error } = useSet();

  const getErrorMessage = (err: any) => {
    if (!err) return "";
    if (typeof err === "string") return err;
    if (typeof err === "boolean") return "An unknown error occurred";
    if (err.error) return err.error;
    if (err.message) return err.message;
    return JSON.stringify(err);
  };

  useEffect(() => {
    if (success) {
      toast.success(`Invoices cleared successfully`);
      setSelectedInvoices([]);
      setPhoneNumber("");
      setClearing(false);
      setTimeout(() => {
        window.location.reload();
        reset();
      }, 1000);
    }

    if (Object?.keys(error)?.length > 0) {
      toast.error(error?.error || "Failed to clear invoices");
      setClearing(false);
    }
  }, [success, error]);

  useEffect(() => {
    getCredentials();
  }, []);

  const handleClose = () => {
    updateData("openAdvanceBalance", false);
    setPhoneNumber("");
    setSelectedInvoices([]);
    reset();
  };

  const handleGetAdvanceBalance = (e: React.FormEvent) => {
    e.preventDefault();
    loadData({
      api: API_CHECK_ADVANCE_BALANCE,
      params: { phone_number: phoneNumber },
    });
  };

  const handleInvoiceSelection = (invoice: any) => {
    setSelectedInvoices((prev) =>
      prev.includes(invoice.invoice_id)
        ? prev.filter((id) => id !== invoice.invoice_id)
        : [...prev, invoice.invoice_id],
    );
  };

  const submitSelectedInvoices = (e: any) => {
    e.preventDefault();
    if (selectedInvoices.length === 0) {
      toast.error("No invoices selected to clear");
      return;
    }
    setClearing(true);
    uploadData({
      api: API_CLEAR_ADVANCES,
      params: {
        phone_number: phoneNumber,
        invoice_ids: selectedInvoices,
      },
    });
  };

  const totalSelectedAmount =
    advanceBalance?.pending_invoices
      ?.filter((inv: any) => selectedInvoices.includes(inv.invoice_id))
      .reduce((sum: number, inv: any) => sum + inv.amount, 0) || 0;

  return (
    <Dialog
      open={!!openAdvanceBalance}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <form onSubmit={handleGetAdvanceBalance}>
        <DialogTitle>Check E-Coupon Balance</DialogTitle>
        <DialogContent>
          {/* Phone input */}
          <PhoneInput
            country="ug"
            value={phoneNumber}
            onChange={(value) => setPhoneNumber(value)}
            placeholder="Enter phone number"
            enableSearch
            containerStyle={{ width: "100%" }}
            inputClass="phone-input-custom"
          />

          {/* Loader for balance */}
          {loading && (
            <Box display="flex" justifyContent="center" mt={3}>
              <CircularProgress size={40} />
            </Box>
          )}

          {/* Advance balance details */}
          {loadSuccess && advanceBalance && (
            <Box mt={3}>
              <Box
                p={2}
                borderRadius={1}
                bgcolor={advanceBalance.is_active ? "#edf7ed" : "#fff3e0"}
                border={1}
                borderColor={advanceBalance.is_active ? "#4caf50" : "#ff9800"}
              >
                <Typography variant="h6">
                  {advanceBalance.is_active ? "Active" : "Inactive"}
                </Typography>
                <Typography>
                  <strong>Phone:</strong> +{advanceBalance.phone_number}
                </Typography>
                <Typography>
                  <strong>Balance:</strong>{" "}
                  {formatAmount(advanceBalance.balance)}
                </Typography>
                <Typography>
                  <strong>Total Cars:</strong> {advanceBalance.total_cars}
                </Typography>
              </Box>

              {advanceBalance.car_numbers?.length > 0 && (
                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    Linked Cars
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {advanceBalance.car_numbers.map((car, index) => (
                      <Chip
                        key={index}
                        label={car.car_number}
                        color={car.is_active ? "primary" : "default"}
                        variant={car.is_active ? "filled" : "outlined"}
                        icon={
                          car.is_active ? <CheckCircleIcon /> : <CancelIcon />
                        }
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Pending invoices */}
              {advanceBalance.pending_invoices?.length > 0 && (
                <Box mt={3}>
                  {((selectedInvoices.length > 0 && role === "Accountant") ||
                    role === "Super Admin" ||
                    role === "Admin") && (
                    <Box
                      mt={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle1">
                        Total Amount: UGX {totalSelectedAmount.toLocaleString()}
                      </Typography>
                      <Button
                        type="primary"
                        loading={clearing || submitting}
                        onClick={submitSelectedInvoices}
                      >
                        Clear Selected Invoices
                      </Button>
                    </Box>
                  )}

                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      mt: 1,
                      borderColor: "var(--lp-border)",
                      borderRadius: "var(--lp-r-md)",
                      boxShadow: "var(--lp-shadow-sm)",
                    }}
                  >
                    {/* the register tables' band and hover, kept in the modal */}
                    <Table
                      size="small"
                      sx={{
                        "& th": {
                          background: "var(--lp-tile)",
                          color: "var(--lp-muted)",
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          borderColor: "var(--lp-border)",
                        },
                        "& td": {
                          fontSize: 13,
                          color: "var(--lp-text)",
                          borderColor: "var(--lp-border)",
                        },
                        "& tbody tr:hover td": {
                          background: "var(--lp-primary-soft)",
                        },
                        "& tbody tr:last-child td": { borderBottom: "none" },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Select</TableCell>
                          <TableCell>Invoice No.</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {advanceBalance.pending_invoices.map((invoice: any) => (
                          <TableRow key={invoice.invoice_id}>
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedInvoices.includes(
                                  invoice.invoice_id,
                                )}
                                onChange={() => handleInvoiceSelection(invoice)}
                              />
                            </TableCell>
                            <TableCell>{invoice.invoice_number}</TableCell>
                            <TableCell>
                              {invoice.amount.toLocaleString()}
                            </TableCell>
                            <TableCell>{invoice.type}</TableCell>
                            <TableCell>
                              {new Date(
                                invoice.created_at,
                              ).toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}

          {statusError && (
            <Box mt={3} color="error.main">
              {getErrorMessage(statusError)}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <MuiButton onClick={handleClose} variant="outlined">
            Close
          </MuiButton>
          <MuiButton type="submit" variant="contained">
            Check Balance
          </MuiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
